import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';
import Button from 'src/componentes/button';
import Dialog from 'src/componentes/dialog';
import LoadingContainer from 'src/componentes/loading-container';
import InputSearch from 'src/componentes/input-search';
import Results from 'src/componentes/NewList/Results';
import useAuth from 'src/hooks/useAuth';
import http from 'src/services/httpService';
import focusManager from 'src/utils/focus-manager';
import { capitalizeFirstLetter, normalizeSearchText } from 'src/utils/string';

const PAGE_SIZE = 20;
const SEARCH_PAGE_SIZE = 500;

export default function PublicoInteressadoPreviewModal({
  documentoId,
  criterios,
  hasLift = false,
  onClose,
  zIndex,
  focusTrapPriority,
}) {
  const intl = useIntl();
  const { terms } = useAuth();
  const dialogRef = useRef();
  const requestId = useRef(0);
  const audienceCache = useRef(null);
  const [preview, setPreview] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const normalizedSearch = normalizeSearchText(searchText.trim());
  const isBusy = isLoading;
  const columns = useMemo(
    () => [
      {
        valueField: 'nome',
        headerText: intl.formatMessage({ id: 'nome' }),
        visible: true,
      },
      {
        valueField: 'cargo',
        headerText: capitalizeFirstLetter(terms.cargo),
        valueFunction: (item) =>
          item.cargos?.map((c) => c.nome).join(', ') || '—',
        visible: hasLift,
      },
      {
        valueField: 'area',
        headerText: capitalizeFirstLetter(terms.area),
        valueFunction: (item) => item.area?.nome || '—',
        visible: true,
      },
    ],
    [intl, terms.cargo, terms.area, hasLift]
  );

  useEffect(() => {
    audienceCache.current = { pages: new Map(), audience: null, total: null };
  }, [documentoId, criterios]);

  const loadPreview = useCallback(
    async (pagina = 1) => {
      const currentRequest = ++requestId.current;
      setIsLoading(true);
      setHasError(false);
      try {
        const cache = audienceCache.current;
        const requestPage = (page, pageSize) => {
          const pageKey = page + ':' + pageSize;
          if (!cache.pages.has(pageKey)) {
            const request = http
              .post('/DocumentoPublicoInteressado/Previsualizar', {
                documentoId: documentoId ?? null,
                criterios,
                pagina: page,
                tamanhoPagina: pageSize,
              })
              .then((response) => {
                const { colaboradores, total } = response.data;
                if (
                  !Array.isArray(colaboradores) ||
                  !Number.isInteger(total) ||
                  total < 0
                ) {
                  throw new Error(
                    intl.formatMessage({
                      id: 'msgErroPreviaPublicoInteressado',
                    })
                  );
                }
                return { colaboradores, total };
              })
              .catch((error) => {
                cache.pages.delete(pageKey);
                throw error;
              });
            cache.pages.set(pageKey, request);
          }
          return cache.pages.get(pageKey);
        };

        const storeAudience = (data) => {
          if (!cache.audience) {
            cache.audience = {
              ...data,
              searchIndex: data.colaboradores.map((colaborador) => ({
                colaborador,
                values: [
                  colaborador.nome,
                  colaborador.cargos?.map((c) => c.nome).join(' '),
                  colaborador.area?.nome,
                ].map(normalizeSearchText),
              })),
            };
          }
          return cache.audience;
        };

        let audience = cache.audience;
        if (
          !normalizedSearch &&
          (!audience || audience.colaboradores.length < audience.total)
        ) {
          const data = await requestPage(pagina, pageSize);
          if (currentRequest !== requestId.current) return;
          cache.total = data.total;
          if (data.colaboradores.length === data.total) storeAudience(data);
          setPreview({ ...data, pagina });
          return;
        }
        if (!audience) {
          const data = await requestPage(
            1,
            Math.max(
              1,
              Math.min(cache.total ?? SEARCH_PAGE_SIZE, SEARCH_PAGE_SIZE)
            )
          );
          if (currentRequest !== requestId.current) return;
          const allColaboradores = [...data.colaboradores];
          let total = data.total;
          const batchSize = data.colaboradores.length;
          let nextPage = 2;
          while (allColaboradores.length < total) {
            if (!batchSize) {
              throw new Error(
                intl.formatMessage({ id: 'msgErroPreviaPublicoInteressado' })
              );
            }
            const next = await requestPage(nextPage, batchSize);
            if (currentRequest !== requestId.current) return;
            if (!next.colaboradores.length) {
              throw new Error(
                intl.formatMessage({ id: 'msgErroPreviaPublicoInteressado' })
              );
            }
            allColaboradores.push(...next.colaboradores);
            total = next.total;
            nextPage += 1;
          }
          cache.total = total;
          audience = storeAudience({ colaboradores: allColaboradores, total });
        }

        const colaboradores = normalizedSearch
          ? audience.searchIndex
              .filter(({ values }) =>
                values.some((text) => text.includes(normalizedSearch))
              )
              .map(({ colaborador }) => colaborador)
          : audience.colaboradores;
        setPreview({
          colaboradores: colaboradores.slice(
            (pagina - 1) * pageSize,
            pagina * pageSize
          ),
          total: colaboradores.length,
          pagina,
        });
      } catch {
        if (currentRequest !== requestId.current) return;
        setPreview(null);
        setHasError(true);
      } finally {
        if (currentRequest === requestId.current) setIsLoading(false);
      }
    },
    [documentoId, criterios, intl, normalizedSearch, pageSize]
  );

  const close = useCallback(() => {
    requestId.current += 1;
    dialogRef.current?.close();
  }, []);

  useEffect(() => {
    loadPreview();
    return () => {
      requestId.current += 1;
    };
  }, [loadPreview]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (
        event.key === 'Escape' &&
        !event.defaultPrevented &&
        focusManager.getTopPriority() === focusTrapPriority
      ) {
        event.preventDefault();
        close();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [close, focusTrapPriority]);

  return createPortal(
    <Dialog
      ref={dialogRef}
      title={intl.formatMessage({ id: 'labelPrevisualizarPublicoInteressado' })}
      ignoreResourceTitle
      textCapitalizeTitle={false}
      titleCapitalizeNone
      width={800}
      maxWidth="95%"
      maxHeight="85%"
      zIndex={zIndex}
      focusTrapPriority={focusTrapPriority}
      onClose={onClose}
      onRequestClose={close}
      padContent
      actions={[
        <Button key="fechar" type="button" color="secondary" onClick={close}>
          {intl.formatMessage({ id: 'label.fechar' })}
        </Button>,
      ]}
    >
      <div className="tw:mt-4 tw:w-full tw:sm:max-w-sm">
        <InputSearch
          type="search"
          placeholder={intl.formatMessage({ id: 'label.pesquisaRapida' })}
          aria-label={intl.formatMessage({ id: 'label.pesquisaRapida' })}
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
        />
      </div>
      <LoadingContainer isLoading={isBusy} className="tw:min-h-40 tw:pb-4">
        <div aria-live="polite" aria-busy={isBusy} className="tw:mt-4">
          {hasError && (
            <p role="alert">
              {intl.formatMessage({ id: 'msgErroPreviaPublicoInteressado' })}
            </p>
          )}
          {preview && (
            <>
              <p>
                {intl.formatMessage(
                  {
                    id:
                      preview.total === 1
                        ? 'msgPreviaPublicoInteressadoSingular'
                        : 'msgPreviaPublicoInteressadoPlural',
                  },
                  {
                    quantidade: preview.total,
                    colaborador: terms.colaborador,
                    colaboradores: terms.colaboradores,
                  }
                )}
              </p>
              <Results
                resources={terms}
                columns={columns}
                data={preview.colaboradores}
                count={preview.total}
                currentPage={preview.pagina}
                pageSize={pageSize}
                isLoading={isBusy}
                showMenu={false}
                showDelete={false}
                onChangeCurrentPage={(page) => {
                  if (!isBusy) loadPreview(page);
                }}
                onChangePageSize={(size) => {
                  if (!isBusy) setPageSize(size);
                }}
                handleRefreshPage={() => {
                  if (isBusy) return;
                  audienceCache.current = {
                    pages: new Map(),
                    audience: null,
                    total: null,
                  };
                  loadPreview();
                }}
              />
            </>
          )}
        </div>

        {hasError && (
          <Button
            type="button"
            color="secondary"
            disabled={isBusy}
            onClick={() => loadPreview()}
          >
            {intl.formatMessage({ id: 'labelPrevisualizarPublicoInteressado' })}
          </Button>
        )}
      </LoadingContainer>
    </Dialog>,
    document.body
  );
}
